import { ArrowRight, X } from '@phosphor-icons/react';
import { HttpStatusCode } from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import { useUser } from '../../../../../../utils/UserContext';
import useApi from '../../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { AccentButton, NeutralButton } from '../../../../../../shared/Buttons';
import InputWithPlacesAPI from '../../../../../signup/customers/InputWithPlacesAPI';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import {
  AddressComponents,
  getFormattedAddressObject,
} from '../../../../../../utils/AddressUtils';
import { Select } from 'antd';
import { US_STATES } from '../../../../../../types/constants';
import { useQueryClient } from '@tanstack/react-query';

export type AddressForm = {
  address: string;
  address_st_number?: string;
  address_st_name?: string;
  address_unit?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
};

export const AddNewAddressModal = ({
  isOpen,
  addresses,
  onClose,
}: {
  isOpen: boolean;
  addresses: any[];
  onClose: () => void;
}) => {
  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm<AddressForm>();
  const { userId } = useUser();
  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [addressFields, setAddressFields] = useState<AddressComponents>({});
  const [addressSelectedFromDropdown, setAddressSelectedFromDropdown] =
    useState(false);

  const [useManualEntry, setUseManualEntry] = useState(false);

  const wNum = watch('address_st_number');
  const wName = watch('address_st_name');
  const wUnit = watch('address_unit');
  const wCity = watch('address_city');
  const wState = watch('address_state');
  const wZip = watch('address_zip');

  const isAddressLimitReached = useMemo(
    () => addresses?.length >= 20,
    [addresses]
  );

  useEffect(() => {
    if (useManualEntry) {
      setAddressFields({
        address_st_number: wNum,
        address_st_name: wName,
        address_unit: wUnit,
        address_city: wCity,
        address_state: wState,
        address_zip: wZip,
      });
    }
  }, [useManualEntry, wNum, wName, wUnit, wCity, wState, wZip]);

  const onSubmit: SubmitHandler<AddressForm> = () => {
    createAddress();
  };

  const createAddress = async () => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        `/addresses`,
        'POST',
        { customer_id: userId, ...addressFields },
        'Error occurred while creating new address.'
      );
      if (response?.status === HttpStatusCode.Created) {
        toast.success('Successfully created new address.');
        queryClient.invalidateQueries({
          queryKey: ['addresses', userId ?? ''],
        });
      } else {
        toast.error('Error occurred while creating new address.');
      }
    } catch (e: any) {
      console.error(
        'Error occurred while creating newly linked account record: ',
        e
      );
    } finally {
      onClose();
      setLoading(false);
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="fixed inset-0 z-[999] flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div className="h-auto max-h-[90%] w-full max-w-[90%] overflow-x-hidden rounded-[4px] bg-white md:max-w-[520px]">
              <div className="flex h-[56px] w-full flex-row items-center justify-between p-4 align-middle">
                <h2 className="text-[16px] font-[600] text-primary">
                  Add New Address
                </h2>
                <X
                  className="hover:cursor-pointer hover:opacity-80"
                  onClick={onClose}
                  weight="regular"
                  size={24}
                />
              </div>

              <form className="flex h-full w-full flex-col items-center gap-3 overflow-y-auto overflow-x-hidden border-[1px] border-bordergray px-4 py-5 pb-4">
                <div className="w-full">
                  {!useManualEntry ? (
                    <>
                      <label
                        htmlFor="address"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Address
                      </label>
                      <InputWithPlacesAPI
                        nameField="address"
                        placeholder="Enter New Address"
                        register={register as any}
                        addressSelectedFromDropdown={
                          addressSelectedFromDropdown
                        }
                        setAddressSelectedFromDropdown={
                          setAddressSelectedFromDropdown
                        }
                        watch={watch}
                        setValue={setValue}
                        defaultValue={watch('address')}
                        onSelectAddress={(
                          formattedAddress,
                          addressComponents
                        ) => {
                          setValue('address', formattedAddress);
                          if (addressComponents) {
                            const formatted =
                              getFormattedAddressObject(addressComponents);
                            setAddressFields(formatted);
                          }
                        }}
                        error={errors.address?.message}
                      />

                      {/* Helper link */}
                      <div className="mt-8 text-sm">
                        <span className="mr-4 text-gray-500">
                          Having trouble finding your address?
                        </span>
                        <button
                          type="button"
                          onClick={() => setUseManualEntry(true)}
                          className="inline-flex items-center gap-1 font-medium text-infoPrimary hover:underline"
                        >
                          Enter address manually{' '}
                          <ArrowRight size={18} className="font-bold" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mb-2 flex items-center">
                        <span className="mr-4 text-sm text-graysecondary">
                          Want to go back?
                        </span>
                        <button
                          type="button"
                          onClick={() => setUseManualEntry(false)}
                          className="inline-flex items-center gap-1 font-medium text-infoPrimary hover:underline"
                        >
                          Back to search{' '}
                          <ArrowRight size={18} className="font-bold" />
                        </button>
                      </div>

                      <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-graysecondary">
                              Street Number
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              {...register('address_st_number', {
                                required: true,
                              })}
                              placeholder="Ex.: 123"
                              className="mt-1 w-full rounded border border-bordergray px-3 py-2"
                            />
                          </div>

                          <div className="md:col-span-4">
                            <label className="block text-sm font-medium text-graysecondary">
                              Street Name<span className="text-danger">*</span>
                            </label>
                            <input
                              {...register('address_st_name', {
                                required: true,
                              })}
                              placeholder="Ex.: Main Street"
                              className="mt-1 w-full rounded border border-bordergray px-3 py-2"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-graysecondary">
                            Address Line 2
                          </label>
                          <input
                            {...register('address_unit')}
                            placeholder="Ex.: Apt 5B"
                            className="mt-1 w-full rounded border border-bordergray px-3 py-2"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-graysecondary">
                            City
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            {...register('address_city', { required: true })}
                            placeholder="Select City"
                            className="mt-1 w-full rounded border border-bordergray px-3 py-2"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-graysecondary">
                            Post Code<span className="text-danger">*</span>
                          </label>
                          <input
                            {...register('address_zip', {
                              required: 'ZIP code is required',
                              onChange: (e) => {
                                const onlyDigits = (e.target.value || '')
                                  .replace(/\D/g, '')
                                  .slice(0, 5);
                                e.target.value = onlyDigits;
                                setValue('address_zip', onlyDigits, {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                });
                                if (useManualEntry) {
                                  setAddressFields((prev) => ({
                                    ...prev,
                                    address_zip: onlyDigits,
                                  }));
                                }
                              },
                            })}
                            placeholder="Ex.: 10001"
                            inputMode="numeric"
                            autoComplete="postal-code"
                            maxLength={5}
                            className={`mt-1 w-full rounded border px-3 py-2 ${
                              errors.address_zip
                                ? 'border-red-500'
                                : 'border-bordergray'
                            }`}
                            onKeyDown={(e) => {
                              // block non-digits (allow nav keys/backspace/delete/tab)
                              const allowed = [
                                'Backspace',
                                'Delete',
                                'Tab',
                                'ArrowLeft',
                                'ArrowRight',
                                'Home',
                                'End',
                              ];
                              if (allowed.includes(e.key)) return;
                              if (!/^\d$/.test(e.key)) e.preventDefault();
                            }}
                          />
                          {errors.address_zip && (
                            <p className="mt-1 text-sm text-red-500">
                              {String(errors.address_zip.message)}
                            </p>
                          )}
                        </div>
                        <div className="w-full">
                          <label
                            htmlFor="address_state"
                            className="block text-sm font-medium text-graysecondary"
                          >
                            State
                            <span className="text-danger">*</span>
                          </label>
                          <Controller
                            name="address_state"
                            control={control}
                            rules={{ required: 'Please select a state' }}
                            render={({ field }) => (
                              <Select
                                id="address_state"
                                className={`mt-1 block h-[42px] min-h-[42px] w-full ${
                                  errors.address_state
                                    ? 'border-red-500'
                                    : 'border-gray-300'
                                }`}
                                placeholder="Select a state"
                                options={US_STATES.map((state) => ({
                                  value: state.abbreviation,
                                  label: state.name,
                                }))}
                                {...field}
                              />
                            )}
                          />
                          {errors.address_state && (
                            <p className="mt-1 text-sm text-red-500">
                              {errors.address_state.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  {isAddressLimitReached ? (
                    <p className="mt-2 text-sm text-danger">
                      You have reached the maximum of 20 addresses allowed.
                      Please remove an existing address before adding a new one.
                    </p>
                  ) : null}
                </div>
              </form>

              {/* Buttons outside the form */}
              <div className="flex justify-between space-x-2 p-4">
                <NeutralButton
                  onClick={onClose}
                  addClass="rounded-full w-[238px] h-[48px] flex flex-row items-center justify-center !border-[#DADADA]"
                >
                  Cancel
                </NeutralButton>
                <AccentButton
                  submit={true}
                  disabled={
                    !isAddressLimitReached && addressFields.address_st_name
                      ? false
                      : true
                  }
                  onClick={handleSubmit(onSubmit)}
                  addClass="rounded-full hover:bg-yellow-600 w-[238px] h-[48px] disabled:opacity-50 disabled:bg-yellow-600 flex flex-row items-center justify-center "
                >
                  {!loading ? (
                    'Add Address'
                  ) : (
                    <ClipLoader
                      color="#1D213B"
                      loading={loading}
                      size={20}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="h-full w-full justify-center"
                    />
                  )}
                </AccentButton>
              </div>
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return <>{renderScreenView(screenView)}</>;
};

export default AddNewAddressModal;
